import { type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import {
  AutoAwesome as MagicIcon,
  Description as FileIcon,
  IntegrationInstructions as IntegrationIcon,
  ContentCopy as CopyIcon,
  ArrowForward as NextIcon,
} from '@mui/icons-material';

const WelcomeCardContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 25%, ${theme.palette.success.main}08 50%, ${theme.palette.info.main}05 75%, ${theme.palette.background.paper} 100%)`,
  border: `2px solid ${theme.palette.primary.main}40`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.success.main}, ${theme.palette.info.main})`,
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1, 0),
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: theme.palette.common.white,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    '50%': {
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
    },
    '100%': {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
  },
}));

const FeatureList = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: theme.spacing(1),
  backgroundColor: `${theme.palette.primary.main}20`,
  color: theme.palette.primary.main,
  flexShrink: 0,
  marginTop: theme.spacing(0.25),
}));

const ActionChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(3),
  alignItems: 'center',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  '&.primary': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&.secondary': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
}));

export const WelcomeCard: FC = () => {
  return (
    <WelcomeCardContainer>
      <CardContent sx={{ p: 4 }}>
        <HeaderSection>
          <IconContainer>
            <MagicIcon sx={{ fontSize: 28 }} />
          </IconContainer>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              color="primary"
              gutterBottom
            >
              🎯 Repository Onboarding Wizard
            </Typography>
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ fontWeight: 500 }}
            >
              Generate ODP catalog files automatically by fetching information
              from external sources
            </Typography>
          </Box>
        </HeaderSection>

        <Typography
          variant="body1"
          sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.7 }}
        >
          This powerful template helps you{' '}
          <strong>streamline the onboarding process</strong> for repositories
          into your ODP catalog. Simply select a repository and watch as we
          automatically gather all the necessary metadata and integration
          details.
        </Typography>

        <FeatureList>
          <FeatureItem>
            <FeatureIcon>
              <IntegrationIcon />
            </FeatureIcon>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary"
                gutterBottom
              >
                🔍 Smart Discovery
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Automatically fetches metadata from TDMS, Sechub, BlackDuck,
                SonarQube, and PagerDuty integrations
              </Typography>
            </Box>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>
              <FileIcon />
            </FeatureIcon>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary"
                gutterBottom
              >
                📝 YAML Generation
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Creates a complete ODP catalog entity YAML file with all
                collected information
              </Typography>
            </Box>
          </FeatureItem>
        </FeatureList>

        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: 'background.default',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            color="primary"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            🚀 How it works:
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>1.</strong> Select your repository from the catalog below
            <br />
            <strong>2.</strong> Review and edit the automatically collected
            metadata
            <br />
            <strong>3.</strong> Choose infrastructure dependencies and provided
            APIs
            <br />
            <strong>4.</strong> Copy the generated YAML or proceed to create a
            Pull Request
          </Typography>
        </Box>

        <ActionChips>
          <Typography variant="body2" color="textSecondary" fontWeight={500}>
            Next steps:
          </Typography>
          <StyledChip
            icon={<CopyIcon />}
            label="Copy Generated YAML"
            size="small"
            className="primary"
          />
          <Typography variant="body2" color="textSecondary">
            or
          </Typography>
          <StyledChip
            icon={<NextIcon />}
            label="Create Pull Request"
            size="small"
            className="secondary"
          />
        </ActionChips>
      </CardContent>
    </WelcomeCardContainer>
  );
};
